import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Send, MessageSquare } from "lucide-react";
import { format } from "date-fns";

interface DirectMessage {
  id: string;
  content: string;
  sender_id: string;
  recipient_id: string;
  read_at: string | null;
  created_at: string;
  sender?: {
    full_name: string;
    email: string;
  };
}

interface User {
  id: string;
  full_name: string;
  email: string;
}

export function DirectMessages({ orgId, currentUserId }: { orgId: string; currentUserId: string }) {
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [messages, setMessages] = useState<DirectMessage[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    loadUsers();
  }, [orgId]);

  useEffect(() => {
    if (selectedUser) {
      loadMessages();
      subscribeToMessages();
    }
  }, [selectedUser]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadUsers = async () => {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, full_name, email')
      .eq('org_id', orgId)
      .neq('id', currentUserId);

    if (error) {
      console.error('Error loading users:', error);
      return;
    }

    setUsers(data || []);
  };

  const loadMessages = async () => {
    if (!selectedUser) return;

    const { data, error } = await supabase
      .from('direct_messages')
      .select(`
        *,
        sender:profiles!direct_messages_sender_id_fkey(full_name, email)
      `)
      .eq('org_id', orgId)
      .or(`and(sender_id.eq.${currentUserId},recipient_id.eq.${selectedUser.id}),and(sender_id.eq.${selectedUser.id},recipient_id.eq.${currentUserId})`)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error loading messages:', error);
      return;
    }

    setMessages(data || []);
    markAsRead();
  };

  const markAsRead = async () => {
    if (!selectedUser) return;

    await supabase
      .from('direct_messages')
      .update({ read_at: new Date().toISOString() })
      .eq('recipient_id', currentUserId)
      .eq('sender_id', selectedUser.id)
      .is('read_at', null);
  };

  const subscribeToMessages = () => {
    if (!selectedUser) return;

    const channel = supabase
      .channel('dm_messages')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'direct_messages',
          filter: `org_id=eq.${orgId}`,
        },
        (payload: any) => {
          const newMsg = payload.new as DirectMessage;
          if (
            (newMsg.sender_id === selectedUser.id && newMsg.recipient_id === currentUserId) ||
            (newMsg.sender_id === currentUserId && newMsg.recipient_id === selectedUser.id)
          ) {
            setMessages((prev) => [...prev, newMsg]);
            if (newMsg.recipient_id === currentUserId) {
              markAsRead();
            }
          }
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  };

  const handleSend = async () => {
    if (!newMessage.trim() || !selectedUser) return;

    setIsSending(true);

    try {
      const { error } = await supabase
        .from('direct_messages')
        .insert({
          org_id: orgId,
          sender_id: currentUserId,
          recipient_id: selectedUser.id,
          content: newMessage.trim(),
        });

      if (error) throw error;

      setNewMessage("");
    } catch (error: any) {
      console.error('Error sending message:', error);
      toast({
        title: "Error",
        description: "Failed to send message",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const getUnreadCount = (userId: string) => {
    return messages.filter(
      (msg) => msg.sender_id === userId && msg.recipient_id === currentUserId && !msg.read_at
    ).length;
  };

  return (
    <div className="flex h-[600px] border rounded-lg overflow-hidden">
      {/* Users List */}
      <div className="w-64 border-r bg-muted/30">
        <div className="p-4 border-b">
          <h3 className="font-semibold">Direct Messages</h3>
        </div>
        <ScrollArea className="h-[calc(600px-57px)]">
          {users.map((user) => {
            const unread = getUnreadCount(user.id);
            return (
              <button
                key={user.id}
                onClick={() => setSelectedUser(user)}
                className={`w-full p-3 flex items-center gap-3 hover:bg-muted transition-colors ${
                  selectedUser?.id === user.id ? 'bg-muted' : ''
                }`}
              >
                <Avatar>
                  <AvatarFallback>
                    {user.full_name?.split(' ').map(n => n[0]).join('') || user.email[0].toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left">
                  <p className="font-medium text-sm">{user.full_name || 'Unnamed'}</p>
                  <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                </div>
                {unread > 0 && (
                  <div className="bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs">
                    {unread}
                  </div>
                )}
              </button>
            );
          })}
          {users.length === 0 && (
            <div className="p-4 text-center text-muted-foreground text-sm">
              No team members yet
            </div>
          )}
        </ScrollArea>
      </div>

      {/* Messages Area */}
      <div className="flex-1 flex flex-col">
        {selectedUser ? (
          <>
            <div className="p-4 border-b flex items-center gap-3">
              <Avatar>
                <AvatarFallback>
                  {selectedUser.full_name?.split(' ').map(n => n[0]).join('') || selectedUser.email[0].toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-medium">{selectedUser.full_name || 'Unnamed'}</p>
                <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
              </div>
            </div>

            <ScrollArea className="flex-1 p-4">
              <div className="space-y-4">
                {messages.map((msg) => {
                  const isOwn = msg.sender_id === currentUserId;
                  return (
                    <div key={msg.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[70%] ${isOwn ? 'bg-primary text-primary-foreground' : 'bg-muted'} rounded-lg p-3`}>
                        <p className="text-sm">{msg.content}</p>
                        <p className={`text-xs mt-1 ${isOwn ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                          {format(new Date(msg.created_at), 'h:mm a')}
                        </p>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            <div className="p-4 border-t flex gap-2">
              <Input
                placeholder="Type a message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
              />
              <Button onClick={handleSend} disabled={isSending || !newMessage.trim()}>
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Select a team member to start messaging</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
